import { useEffect, useState } from 'react';
import { RefreshCw } from 'lucide-react';
import { apiClient } from '../../packages/api-client';
import { PageHeader, StateView } from '../../packages/ui';
import { usePageTitle } from '../../hooks/usePageTitle';
import './platform.css';
import './websiteTraffic.css';

type Window = 'today' | '7d' | '30d';
interface Count { label: string; count: number }
interface Day { date: string; pageviews: number; visitors: number }
export interface TrafficReport {
    window: Window; since: string; until: string; fetched_at: string;
    summary: { pageviews: number; visitors: number; signup_clicks: number; verification_requests: number; workspaces_created: number; app_store_clicks: number };
    daily: Day[]; pages: Count[]; sources: Count[]; devices: Count[];
}
type State = { kind: 'loading' } | { kind: 'error' | 'unconfigured' } | { kind: 'ready'; report: TrafficReport };
const number = (value: number) => value.toLocaleString();
const dateLabel = (date: string) => new Date(`${date}T00:00:00Z`).toLocaleDateString(undefined, { month: 'short', day: 'numeric', timeZone: 'UTC' });

function Breakdown({ title, rows }: { title: string; rows: Count[] }) {
    const max = Math.max(1, ...rows.map(row => row.count));
    return <section className="traffic-panel" aria-label={title}>
        <div className="platform-section-head"><h2>{title}</h2><span className="platform-hint">Pageviews</span></div>
        {rows.length === 0 ? <p className="platform-hint">No pageviews in this period.</p> :
            <ol className="traffic-ranking">{rows.map(row => <li key={row.label}>
                <div className="traffic-rank-label"><span>{row.label}</span><strong>{number(row.count)}</strong></div>
                <div className="traffic-meter" aria-hidden="true"><span style={{ width: `${row.count / max * 100}%` }} /></div>
            </li>)}</ol>}
    </section>;
}

function DailyTraffic({ days }: { days: Day[] }) {
    const max = Math.max(1, ...days.map(day => Math.max(day.pageviews, day.visitors)));
    const slot = 600 / Math.max(1, days.length);
    return <section className="traffic-panel" aria-label="Daily traffic">
        <div className="platform-section-head"><h2>Daily traffic</h2><div className="traffic-legend"><span>Pageviews</span><span>Visitor estimates</span></div></div>
        <svg className="traffic-chart" viewBox="0 0 600 150" role="img" aria-label="Daily pageviews and visitor estimates; exact values are available in Daily figures below">
            <line x1="0" y1="140" x2="600" y2="140" stroke="var(--border)" />
            {days.map((day, i) => <g key={day.date}>
                <title>{dateLabel(day.date)}: {number(day.pageviews)} pageviews, {number(day.visitors)} visitor estimates</title>
                <rect x={i * slot + slot * 0.12} y={140 - day.pageviews / max * 130} width={slot * 0.34} height={day.pageviews / max * 130} rx="2" fill="var(--accent)" />
                <rect x={i * slot + slot * 0.52} y={140 - day.visitors / max * 130} width={slot * 0.34} height={day.visitors / max * 130} rx="2" fill="var(--ink-muted)" />
            </g>)}
        </svg>
        {days.length > 0 && <div className="traffic-chart-dates"><span>{dateLabel(days[0].date)}</span><span>{dateLabel(days[days.length - 1].date)} · UTC</span></div>}
        <details className="traffic-daily-details"><summary>Daily figures</summary>
            <table className="traffic-table"><thead><tr><th scope="col">Date (UTC)</th><th scope="col">Pageviews</th><th scope="col">Visitors</th></tr></thead>
                <tbody>{days.map(day => <tr key={day.date}><th scope="row">{dateLabel(day.date)}</th><td>{number(day.pageviews)}</td><td>{number(day.visitors)}</td></tr>)}</tbody>
            </table>
        </details>
    </section>;
}

export function WebsiteTrafficPage() {
    usePageTitle('Website traffic');
    const [window, setWindow] = useState<Window>('7d');
    const [refresh, setRefresh] = useState(0);
    const [state, setState] = useState<State>({ kind: 'loading' });
    useEffect(() => {
        const controller = new AbortController();
        setState({ kind: 'loading' });
        apiClient.get<TrafficReport>('/admin/website-traffic/', { params: { window }, signal: controller.signal })
            .then(response => { if (!controller.signal.aborted) setState({ kind: 'ready', report: response.data }); })
            .catch((error: { response?: { data?: { code?: string } } }) => {
                if (!controller.signal.aborted) setState({ kind: error.response?.data?.code === 'ANALYTICS_NOT_CONFIGURED' ? 'unconfigured' : 'error' });
            });
        return () => controller.abort();
    }, [window, refresh]);
    const retry = () => setRefresh(value => value + 1);
    const report = state.kind === 'ready' ? state.report : undefined;
    return <div className="website-traffic">
        <PageHeader breadcrumb={[{ label: 'Platform console' }, { label: 'Website traffic' }]} title="Website traffic"
            subtitle="Visits to the public website and progress through signup."
            actions={<button type="button" className="btn btn-secondary btn-md" onClick={retry} disabled={state.kind === 'loading'}><RefreshCw size={16} aria-hidden="true" /> Refresh</button>} />
        <div className="platform-toolbar traffic-toolbar"><div className="form-group">
            <label htmlFor="traffic-window">Period</label>
            <select id="traffic-window" className="form-input" value={window} onChange={event => setWindow(event.target.value as Window)}>
                <option value="today">Today (UTC)</option><option value="7d">Last 7 days</option><option value="30d">Last 30 days</option>
            </select>
        </div><p className="platform-hint">Date ranges include today. Figures refresh at most every five minutes.</p></div>
        {state.kind === 'loading' && <StateView state="loading" label="Loading website traffic" />}
        {state.kind === 'unconfigured' && <StateView state="empty" title="Website analytics is not connected yet" message="Traffic reports will appear here once the server connection is configured." />}
        {state.kind === 'error' && <StateView state="error" title="Website traffic unavailable" message="We could not load the traffic figures. Try again shortly." onRetry={retry} />}
        {report && <>
            <p className="platform-fresh traffic-fresh">Updated {new Date(report.fetched_at).toLocaleString()}. Includes events through {new Date(report.until).toLocaleString()}.</p>
            <dl className="traffic-metrics platform-metrics">
                {[
                    { label: 'Visitor estimates', value: report.summary.visitors, meaning: 'Cookieless IDs reset daily. A returning visitor can count again on another day.' },
                    { label: 'Pageviews', value: report.summary.pageviews, meaning: 'Views across the public website, including repeat visits.' },
                    { label: 'Signup starts', value: report.summary.signup_clicks, meaning: 'Clicks on links to create a workspace.' },
                    { label: 'Workspaces created', value: report.summary.workspaces_created, meaning: 'Tracked successful email verification and workspace creation.' },
                ].map(metric => <div className="stat-card" key={metric.label}><dt className="stat-label">{metric.label}</dt><dd className="stat-value">{number(metric.value)}</dd><dd className="platform-metric-def">{metric.meaning}</dd></div>)}
            </dl>
            {report.summary.pageviews === 0 && <p className="traffic-empty" role="status">No website visits recorded in this period yet.</p>}
            <DailyTraffic days={report.daily} />
            <div className="traffic-breakdowns"><Breakdown title="Top pages" rows={report.pages} /><Breakdown title="Traffic sources" rows={report.sources} /><Breakdown title="Devices" rows={report.devices} /></div>
            <section className="traffic-panel" aria-label="Signup and app activity"><h2>Signup and app activity</h2>
                <dl className="traffic-activity">
                    <div><dt>Verification emails requested</dt><dd>{number(report.summary.verification_requests)}</dd></div>
                    <div><dt>Workspaces created</dt><dd>{number(report.summary.workspaces_created)}</dd></div>
                    <div><dt>App Store link clicks</dt><dd>{number(report.summary.app_store_clicks)}</dd></div>
                </dl>
                <p className="platform-hint">Activity counts describe separate events, not a linked signup funnel. Privacy settings and blockers can prevent tracking.</p>
            </section>
            <p className="platform-hint">Public-site analytics only. Setup-check events are excluded. Traffic sources use campaign source tags when present, otherwise the referring domain.</p>
        </>}
    </div>;
}
